import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { PRIMARY_NAV } from '@/content/routes';
import { LogoMark } from './Logo';
import styles from './Header.module.css';

/**
 * Sticky site header.
 *
 * Two departures from the source design, both required:
 *
 * 1. The design marked the current page with colour alone. Colour cannot be the
 *    only carrier of meaning (SC 1.4.1), so `aria-current="page"` carries it too.
 * 2. The design had no small-viewport treatment at all. Below 880px the nav
 *    collapses into a disclosure panel behind a labelled toggle.
 */
export function Header() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) toggleRef.current?.focus();
  }, []);

  // Navigating away should never leave the panel hanging open - including via
  // browser back/forward, which no click handler would catch. Adjusting state
  // during render is React's documented pattern for this; an effect would
  // paint the stale open panel first.
  const [renderedAt, setRenderedAt] = useState(pathname);
  if (renderedAt !== pathname) {
    setRenderedAt(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    // Focus must be able to leave the panel by any means, not just Escape.
    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  const isCurrent = (path: string) => (pathname === path ? 'page' : undefined);

  return (
    <header className={styles.header} ref={headerRef}>
      <div className={styles.inner}>
        <Link to="/" className={`${styles.brand} sk-hit`} aria-label="Skabro - home">
          <LogoMark size={24} />
          <span className={styles.brandName}>Skabro</span>
        </Link>

        <nav className={styles.nav} aria-label="Primary">
          {PRIMARY_NAV.map((route) => (
            <Link
              key={route.path}
              to={route.path}
              className={styles.navLink}
              aria-current={isCurrent(route.path)}
            >
              {route.label}
            </Link>
          ))}
        </nav>

        <Link to="/contact" className={styles.cta}>
          Start a conversation
        </Link>

        <button
          type="button"
          ref={toggleRef}
          className={styles.toggle}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            setOpen((v) => !v);
          }}
        >
          <span className={styles.toggleBars} aria-hidden="true">
            <span />
          </span>
          {/* A stable name, with aria-expanded carrying the state. Swapping the
              name to "Close menu" would leave a voice-control user unable to
              say the same phrase twice. */}
          <span className="sk-visually-hidden">Menu</span>
        </button>
      </div>

      {open && (
        <div className={styles.panel} id={panelId}>
          <nav className={styles.panelInner} aria-label="Primary, mobile">
            {PRIMARY_NAV.map((route) => (
              <Link
                key={route.path}
                to={route.path}
                className={styles.panelLink}
                aria-current={isCurrent(route.path)}
              >
                {route.label}
              </Link>
            ))}
            <Link to="/contact" className={styles.panelCta}>
              Start a conversation
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
